import { openDB, type IDBPDatabase } from 'idb'
import type { ReportSource } from '../domain/types'

/**
 * Where a session lives between visits.
 *
 * IndexedDB rather than localStorage: a session can hold a hundred reports at
 * roughly 20 kB each, which is well past what localStorage will take.
 *
 * Reports are stored as the original document plus where it came from, never as
 * the normalised form. Normalisation is expected to improve -- a new output
 * vocabulary, a corrected dimension alias -- and storing the raw document means
 * those improvements apply to everything already saved, instead of leaving old
 * entries frozen in whatever the loader understood on the day they arrived.
 */

const DB_NAME = 'everse-quality-dashboard'
const DB_VERSION = 1

const REPORTS = 'reports'
const META = 'meta'

export interface StoredReport {
  id: string
  raw: unknown
  source: ReportSource
  addedAt: number
}

export class QuotaExceeded extends Error {
  constructor() {
    super('The browser will not store any more data for this site.')
    this.name = 'QuotaExceeded'
  }
}

let handle: Promise<IDBPDatabase> | undefined

function db(): Promise<IDBPDatabase> {
  handle ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database, oldVersion) {
      // Written as a sequence of steps rather than a single create, so a future
      // version can add to it without rewriting what is already on disk.
      if (oldVersion < 1) {
        database.createObjectStore(REPORTS, { keyPath: 'id' })
        database.createObjectStore(META)
      }
    },
    // A newer version opened in another tab needs this one out of the way;
    // the next access reopens.
    blocking() {
      void handle?.then((database) => database.close())
      handle = undefined
    },
    terminated() {
      handle = undefined
    },
  }).catch((error: unknown) => {
    // Not cached: a failed open (storage briefly unavailable) is retried on
    // the next access instead of failing for the rest of the visit.
    handle = undefined
    throw error
  })
  return handle
}

/**
 * Every stored report, oldest first.
 *
 * Throws when storage cannot be read, rather than returning nothing: an empty
 * list would look like an empty session, and the user would never learn that
 * what they saved is still there but unreadable from here.
 */
export async function allReports(): Promise<StoredReport[]> {
  const stored = (await (await db()).getAll(REPORTS)) as StoredReport[]
  return stored.sort((a, b) => a.addedAt - b.addedAt)
}

export async function putReports(reports: StoredReport[]): Promise<void> {
  if (reports.length === 0) return
  try {
    const database = await db()
    const tx = database.transaction(REPORTS, 'readwrite')
    await Promise.all(reports.map((report) => tx.store.put(report)))
    await tx.done
  } catch (error) {
    if (isQuotaError(error)) throw new QuotaExceeded()
    throw error
  }
}

export async function deleteReport(id: string): Promise<void> {
  try {
    await (await db()).delete(REPORTS, id)
  } catch {
    // Nothing useful to do; the entry is gone from the view either way.
  }
}

export async function clearReports(): Promise<void> {
  try {
    await (await db()).clear(REPORTS)
  } catch {
    // As above.
  }
}

export async function readMeta<T>(key: string): Promise<T | undefined> {
  try {
    return (await (await db()).get(META, key)) as T | undefined
  } catch {
    return undefined
  }
}

/**
 * Everything the interface currently remembers, as one object.
 *
 * Read wholesale rather than key by key so that exporting a session does not
 * carry a list of key names that has to be updated whenever a new preference
 * starts being stored. A preference that persists is a preference that travels,
 * with no further work.
 */
export async function allMeta(): Promise<Record<string, unknown>> {
  try {
    const database = await db()
    const tx = database.transaction(META, 'readonly')
    const [keys, values] = await Promise.all([tx.store.getAllKeys(), tx.store.getAll()])
    await tx.done

    // No prototype: a key named __proto__, stored from an imported session,
    // would otherwise become this object's prototype rather than an entry.
    const out: Record<string, unknown> = Object.create(null)
    keys.forEach((key, index) => {
      if (typeof key === 'string') out[key] = values[index]
    })
    return out
  } catch {
    return {}
  }
}

/**
 * The counterpart, for restoring an imported session's view state. Replaces
 * the whole store in one transaction, so a setting the file does not carry
 * goes back to its default instead of surviving from the session it replaces.
 */
export async function replaceAllMeta(entries: Record<string, unknown>): Promise<void> {
  try {
    const database = await db()
    const tx = database.transaction(META, 'readwrite')
    await tx.store.clear()
    await Promise.all(Object.entries(entries).map(([key, value]) => tx.store.put(value, key)))
    await tx.done
  } catch (error) {
    if (isQuotaError(error)) throw new QuotaExceeded()
    // Restoring an imported session's settings is part of the import, so a
    // failure is the caller's to report, not something to swallow here.
    throw error
  }
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  try {
    await (await db()).put(META, value, key)
    announceMeta(key)
  } catch (error) {
    if (isQuotaError(error)) throw new QuotaExceeded()
    // View state is not worth failing the application over.
  }
}

/**
 * Forgets every view setting, keeping the reports. The way out when a stored
 * setting is what keeps the application from rendering.
 */
export async function clearMeta(): Promise<void> {
  await (await db()).clear(META)
}

/**
 * Browsers disagree on how a full quota is reported: a named DOMException in
 * some, a legacy numeric code in others.
 */
function isQuotaError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false
  return error.name === 'QuotaExceededError' || error.code === 22
}

/*
 * Other tabs of this application share the same storage. A setting written
 * here is announced, so they read it again rather than later overwriting it
 * with what they held: a removal made in one tab would otherwise be undone by
 * the next sync in another.
 */
const channel =
  typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel('everse-meta')

function announceMeta(key: string): void {
  channel?.postMessage(key)
}

/** Calls back with the key of each setting another tab writes. */
export function onMetaWrittenElsewhere(listener: (key: string) => void): () => void {
  if (!channel) return () => undefined
  const handler = (event: MessageEvent) => {
    if (typeof event.data === 'string') listener(event.data)
  }
  channel.addEventListener('message', handler)
  return () => channel.removeEventListener('message', handler)
}
